import React, { useEffect, useState } from 'react';
import { toast } from 'react-hot-toast';
import {
  XMarkIcon,
  VideoCameraIcon,
  GlobeAltIcon,
  ArrowTopRightOnSquareIcon,
  EyeIcon,
  HandThumbUpIcon,
  ChatBubbleLeftIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  ClockIcon,
  ArrowPathIcon,
  CalendarIcon,
  DocumentIcon,
} from '@heroicons/react/24/outline';
import { videoApi, VideoDetailsResponse, VideoDetailsChannel } from '@/services/videoApi';
import { getLanguageName } from '@/utils/videoTranslationConfig';

const formatBytes = (bytes?: number): string => {
  if (!bytes) return '—';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
};

const formatCount = (n: number): string => {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return String(n);
};

const statusStyles: Record<string, string> = {
  completed: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
  failed: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
  processing: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
  pending: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
  scheduled: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400',
};

const statusIcon = (status: string) => {
  switch (status) {
    case 'completed':
      return <CheckCircleIcon className="h-3.5 w-3.5" />;
    case 'failed':
      return <ExclamationCircleIcon className="h-3.5 w-3.5" />;
    case 'processing':
      return <ArrowPathIcon className="h-3.5 w-3.5 animate-spin" />;
    default:
      return <ClockIcon className="h-3.5 w-3.5" />;
  }
};

const StatusBadge: React.FC<{ status: string }> = ({ status }) => (
  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium capitalize ${statusStyles[status] || 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300'}`}>
    {statusIcon(status)}
    {status}
  </span>
);

const platformMeta: Record<string, { label: string; icon: React.ComponentType<React.SVGProps<SVGSVGElement>>; color: string }> = {
  youtube: { label: 'YouTube', icon: VideoCameraIcon, color: 'text-red-600 dark:text-red-400' },
  facebook: { label: 'Facebook', icon: GlobeAltIcon, color: 'text-blue-600 dark:text-blue-400' },
  instagram: { label: 'Instagram', icon: GlobeAltIcon, color: 'text-pink-600 dark:text-pink-400' },
  whatsapp: { label: 'WhatsApp', icon: GlobeAltIcon, color: 'text-green-600 dark:text-green-400' },
  tiktok: { label: 'TikTok', icon: GlobeAltIcon, color: 'text-gray-800 dark:text-gray-200' },
  x: { label: 'X', icon: GlobeAltIcon, color: 'text-gray-800 dark:text-gray-200' },
};

interface VideoDetailsDrawerProps {
  type: 'upload' | 'translation';
  id: string;
  onClose: () => void;
}

const VideoDetailsDrawer: React.FC<VideoDetailsDrawerProps> = ({ type, id, onClose }) => {
  const [data, setData] = useState<VideoDetailsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    setData(null);
    setPreviewUrl(null);
    videoApi.getVideoDetails(type, id)
      .then((res) => setData(res))
      .catch((err: any) => setError(err?.response?.data?.message || 'Failed to load video details'))
      .finally(() => setLoading(false));

    const fetchPreview = type === 'upload' ? videoApi.getUploadShareableUrl(id) : videoApi.getJobShareableUrl(id);
    fetchPreview
      .then((res) => setPreviewUrl(res.url))
      .catch(() => {
        // No preview available (e.g. video expired from storage) — not fatal, details still render.
      });
  }, [type, id]);

  const copyUrl = async (url?: string) => {
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      toast.success('URL copied to clipboard');
    } catch {
      toast.error('Failed to copy URL');
    }
  };

  const video = data?.video;
  const channels = data?.channels || [];

  return (
    <div className="fixed inset-0 z-[60] flex justify-end">
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-sm transition-opacity"
        onClick={onClose}
        aria-hidden="true"
      />
      <div className="relative h-full w-full md:w-[85vw] lg:w-[1100px] xl:w-[1200px] bg-white dark:bg-gray-900 shadow-2xl flex flex-col">
        <div className="flex items-start justify-between gap-4 border-b border-gray-200 dark:border-gray-700 px-6 py-4">
          <div className="min-w-0">
            <p className="text-xs font-semibold uppercase tracking-wide text-indigo-600 dark:text-indigo-400">
              {type === 'upload' ? 'Uploaded video' : 'Translated video'}
            </p>
            <h2 className="mt-0.5 text-lg font-semibold text-gray-900 dark:text-white truncate">
              {video?.title || 'Video details'}
            </h2>
            {video && (
              <div className="mt-1 flex items-center gap-2">
                <StatusBadge status={video.status} />
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="shrink-0 p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-500 dark:text-gray-300"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="py-16 text-center text-gray-500 dark:text-gray-400">Loading video details…</div>
          ) : error || !data || !video ? (
            <div className="py-16 text-center">
              <ExclamationCircleIcon className="h-10 w-10 text-red-400 mx-auto mb-3" />
              <p className="text-gray-600 dark:text-gray-300">{error || 'Video not found'}</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Preview + details */}
              <div className="lg:col-span-2 space-y-6">
                <div className="bg-black rounded-xl overflow-hidden aspect-video flex items-center justify-center">
                  {previewUrl ? (
                    <video key={previewUrl} src={previewUrl} controls playsInline className="w-full h-full object-contain" />
                  ) : (
                    <div className="text-gray-500 text-sm flex flex-col items-center gap-2">
                      <VideoCameraIcon className="h-10 w-10" />
                      Preview unavailable
                    </div>
                  )}
                </div>

                <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-5">
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">Details</h3>
                  <dl className="grid grid-cols-2 gap-4 text-sm">
                    <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400">
                      <DocumentIcon className="h-4 w-4" /> Size
                    </div>
                    <dd className="text-gray-900 dark:text-white text-right">{formatBytes(video.size)}</dd>

                    <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400">
                      <CalendarIcon className="h-4 w-4" /> Created
                    </div>
                    <dd className="text-gray-900 dark:text-white text-right">{new Date(video.createdAt).toLocaleString()}</dd>

                    {video.mimeType && (
                      <>
                        <div className="text-gray-500 dark:text-gray-400">Format</div>
                        <dd className="text-gray-900 dark:text-white text-right">{video.mimeType}</dd>
                      </>
                    )}

                    {video.sourceLanguage && (
                      <>
                        <div className="text-gray-500 dark:text-gray-400">Source language</div>
                        <dd className="text-gray-900 dark:text-white text-right">{getLanguageName(video.sourceLanguage)}</dd>
                      </>
                    )}

                    {video.targetLanguage && (
                      <>
                        <div className="text-gray-500 dark:text-gray-400">Target language</div>
                        <dd className="text-gray-900 dark:text-white text-right">{getLanguageName(video.targetLanguage)}</dd>
                      </>
                    )}
                  </dl>
                </div>
              </div>

              {/* Published channels */}
              <div className="lg:col-span-1">
                <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-5">
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">
                    Published Channels
                    <span className="ml-2 text-xs font-normal text-gray-400">({channels.length})</span>
                  </h3>

                  {channels.length === 0 ? (
                    <p className="text-sm text-gray-500 dark:text-gray-400 py-4 text-center">
                      Not published to any channel yet.
                    </p>
                  ) : (
                    <div className="space-y-3">
                      {channels.map((channel: VideoDetailsChannel, idx: number) => {
                        const meta = platformMeta[channel.platform] || platformMeta.youtube;
                        const Icon = meta.icon;
                        return (
                          <div
                            key={`${channel.platform}-${idx}`}
                            className="rounded-lg border border-gray-200 dark:border-gray-700 p-3"
                          >
                            <div className="flex items-center justify-between gap-2">
                              <div className="flex items-center gap-2 min-w-0">
                                <Icon className={`h-4 w-4 shrink-0 ${meta.color}`} />
                                <span className="text-sm font-medium text-gray-900 dark:text-white truncate">
                                  {meta.label}
                                </span>
                              </div>
                              <StatusBadge status={channel.status} />
                            </div>
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 truncate">{channel.channelName}</p>
                            {channel.publishedAt && (
                              <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                                {new Date(channel.publishedAt).toLocaleString()}
                              </p>
                            )}

                            {channel.metrics ? (
                              <div className="flex items-center gap-3 mt-2 text-xs text-gray-600 dark:text-gray-300">
                                <span className="flex items-center gap-1" title="Views">
                                  <EyeIcon className="h-3.5 w-3.5" /> {formatCount(channel.metrics.views)}
                                </span>
                                <span className="flex items-center gap-1" title="Likes">
                                  <HandThumbUpIcon className="h-3.5 w-3.5" /> {formatCount(channel.metrics.likes)}
                                </span>
                                <span className="flex items-center gap-1" title="Comments">
                                  <ChatBubbleLeftIcon className="h-3.5 w-3.5" /> {formatCount(channel.metrics.comments)}
                                </span>
                              </div>
                            ) : (
                              channel.status === 'completed' && (
                                <p className="text-xs text-gray-400 dark:text-gray-500 mt-2">Metrics not available</p>
                              )
                            )}

                            <div className="flex items-center gap-3 mt-2">
                              {channel.url && (
                                <a
                                  href={channel.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center gap-1 text-xs text-indigo-600 dark:text-indigo-400 hover:underline"
                                >
                                  <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5" /> View
                                </a>
                              )}
                              {channel.url && (
                                <button
                                  onClick={() => copyUrl(channel.url)}
                                  className="text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
                                >
                                  Copy link
                                </button>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default VideoDetailsDrawer;
